import React, { useEffect, useRef, createRef, forwardRef } from "react";
import {
  Wrapper,
  Container,
  Image,
  StyledMarqueeItem,
  MarqueeContainer,
  MarqueeWrapper,
} from "../styles/components/body";
import animations from "../animations/animations";

const MarqueeItem = forwardRef(
  ({ image, desktopheight, mobileheight }, ref) => (
    <StyledMarqueeItem
      ref={ref}
      $desktopHeight={desktopheight}
      $mobileHeight={mobileheight}
    >
      <MarqueeContainer>
        {image && <Image src={image.fluid?.src} alt={image?.alt || "image"} />}
      </MarqueeContainer>
    </StyledMarqueeItem>
  )
);

const Marquee = ({
  marqueeId,
  parentWrapperRef,
  images,
  desktopHeight,
  mobileHeight = 25,
  direction = "left",
  marqueeDuration = 30,
  triggerScrub,
  articleId,
  isMobile,
  isDesktop,
}) => {
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const marqueeWrapperRef = useRef(null);
  const gsapCtx = useRef(null);
  const length = images?.length;

  const itemRefs = useRef([]);

  itemRefs.current = Array(length)
    .fill(null)
    .map((_, i) => itemRefs.current[i] || createRef());

  const renderMarqueeItems = () => {
    if (images?.length > 0) {
      return images.map((image, i) => (
        <MarqueeItem
          ref={itemRefs.current[i]}
          key={`marquee-item-image-${i}`}
          image={image}
          desktopheight={parseInt(desktopHeight)}
          mobileheight={parseInt(mobileHeight)}
        />
      ));
    }
  };

  useEffect(() => {
    if (!containerRef.current || typeof window === "undefined") return;

    const ctx = animations.context(() => {
      const cloneMarqueeItems = () => {
        const items = containerRef.current.children;
        const totalWidth = Array.from(items).reduce(
          (acc, item) => acc + item.offsetWidth,
          0
        );

        Array.from(items).forEach(item => {
          const clone = item.cloneNode(true);
          containerRef.current.appendChild(clone);
        });

        if (direction === "right") {
          animations.set(containerRef.current, {
            x: `-=${totalWidth}`,
          });
        }

        if (triggerScrub) {
          animations.to(containerRef.current, {
            scrollTrigger: {
              trigger: wrapperRef.current,
              start: "5% bottom",
              end: "max",
              invalidateOnRefresh: true,
              scrub: true,
            },
            duration: marqueeDuration,
            x: direction === "left" ? `-=${totalWidth}` : `+=${totalWidth}`,
            ease: "none",
            modifiers: {
              x: animations.utils.unitize(x => parseFloat(x) % totalWidth),
            },
          });
        }
      };

      setTimeout(cloneMarqueeItems, 500);
    }, containerRef);

    return () => ctx.revert();
  }, [images, direction, marqueeDuration, triggerScrub]);

  useEffect(() => {
    if (
      !parentWrapperRef.current ||
      !wrapperRef.current ||
      typeof window === "undefined"
    )
      return;

    if (!gsapCtx.current) {
      gsapCtx.current = animations.context(() => {});
    }

    gsapCtx.current.add(() => {
      animations
        .timeline({
          scrollTrigger: {
            trigger: marqueeWrapperRef.current,
            start: "top top",
            end: "bottom bottom-=800px",
            scrub: true,
          },
        })
        .to(wrapperRef.current, {
          filter: "blur(10px)",
        });
    });
  }, [parentWrapperRef, wrapperRef]);

  return (
    <Wrapper
      ref={wrapperRef}
      $marqueePosition={{ articleId, marqueeId }}
      $isMobile={isMobile}
      $isDesktop={isDesktop}
    >
      <Container
        ref={containerRef}
        $desktopHeight={parseInt(desktopHeight)}
        $mobileHeight={parseInt(mobileHeight)}
      >
        <MarqueeWrapper ref={marqueeWrapperRef}>
          {renderMarqueeItems()}
        </MarqueeWrapper>
      </Container>
    </Wrapper>
  );
};

export { Marquee };
